import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBarChart, Sparkles, Copy, Printer, Trophy, AlertTriangle, Target, MessageSquareWarning, Loader2 } from "lucide-react";
import { API, apiError } from "../lib/api";
import { PageHeader, Spinner, StatCard } from "../components/common";
import { formatINR } from "../lib/helpers";

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ value: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`, label: m.toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
}

function Section({ icon: Icon, title, color, children, testid }) {
  return (
    <div className="mb-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon className={`h-4 w-4 ${color}`} /><span className="font-heading font-semibold text-slate-100">{title}</span></div>
      {children}
    </div>
  );
}

function reportText(r) {
  const d = r.data, n = r.narrative;
  return [
    `MB NEXUS — Monthly Report: ${r.month_label}`, n.headline, "", n.summary, "",
    `Leads: ${d.leads_created} · Hot: ${d.hot_leads} · Assigned: ${d.assigned} · Meetings: ${d.meetings} · Site visits: ${d.site_visits}`,
    `Deals won: ${d.deals_won} · Revenue: ${formatINR(d.revenue)}`, "",
    "Wins:", ...n.wins.map((w) => `• ${w}`), "", "Concerns:", ...n.concerns.map((w) => `• ${w}`), "",
    `Objections: ${n.objection_analysis}`, "", "Next month focus:", ...n.next_month_focus.map((w) => `• ${w}`),
  ].join("\n");
}

export default function Reports() {
  const months = monthOptions();
  const [month, setMonth] = useState(months[0].value);
  const [list, setList] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadList = () => API.get("/reports").then((r) => setList(r.data));
  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    setLoading(true); setReport(null);
    API.get(`/reports/${month}`).then((r) => setReport(r.data)).catch(() => setReport(null)).finally(() => setLoading(false));
  }, [month]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post(`/reports/${month}/generate`);
      setReport(data); loadList();
      toast.success(`Report for ${data.month_label} generated`);
    } catch (e) { toast.error(apiError(e)); } finally { setGenerating(false); }
  };

  const copy = async () => { await navigator.clipboard.writeText(reportText(report)); toast.success("Report copied to clipboard"); };

  if (!list) return <Spinner />;
  const d = report?.data, n = report?.narrative;

  return (
    <div>
      <PageHeader title="Monthly Reports" subtitle="Auto-generated month-end performance & objection reports." testid="reports-header">
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-select"
            className="bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40">
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}{list.some((r) => r.month === m.value) ? " ✓" : ""}</option>)}
          </select>
          <button onClick={generate} disabled={generating} data-testid="generate-report-btn" className="btn-gold px-4 py-2 text-sm flex items-center gap-2">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{report ? "Regenerate" : "Generate"}
          </button>
        </div>
      </PageHeader>

      {loading && <Spinner />}
      {!loading && !report && (
        <div className="mb-card p-10 text-center" data-testid="report-empty">
          <FileBarChart className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300 font-heading font-semibold">No report for this month yet</div>
          <div className="text-sm text-slate-500 mt-1">Reports auto-generate on the 1st of every month. Generate one now to preview.</div>
        </div>
      )}

      {report && (
        <div className="space-y-6" data-testid="report-view">
          <div className="mb-card p-6 border-amber-500/20">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="caption-mono mb-1">{report.month_label} · generated {new Date(report.generated_at).toLocaleString("en-IN")} · {report.source}</div>
                <h2 className="font-heading text-xl font-bold text-amber-300" data-testid="report-headline">{n.headline}</h2>
                <p className="text-sm text-slate-300 mt-3 leading-relaxed max-w-3xl" data-testid="report-summary">{n.summary}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={copy} data-testid="copy-report-btn" className="px-3 py-2 rounded-lg border border-slate-700 text-slate-200 text-sm hover:bg-slate-800 flex items-center gap-2"><Copy className="h-4 w-4" />Copy</button>
                <button onClick={() => window.print()} data-testid="print-report-btn" className="px-3 py-2 rounded-lg border border-slate-700 text-slate-200 text-sm hover:bg-slate-800 flex items-center gap-2"><Printer className="h-4 w-4" />Print</button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <StatCard label="New Leads" value={d.leads_created} testid="rep-leads" />
            <StatCard label="Hot Leads" value={d.hot_leads} accent="red" testid="rep-hot" />
            <StatCard label="Assigned" value={d.assigned} testid="rep-assigned" />
            <StatCard label="Follow-ups" value={d.followups_sent} accent="blue" testid="rep-followups" />
            <StatCard label="Meetings" value={d.meetings} accent="blue" testid="rep-meetings" />
            <StatCard label="Site Visits" value={d.site_visits} accent="blue" testid="rep-visits" />
            <StatCard label="Deals Won" value={d.deals_won} accent="emerald" testid="rep-won" />
            <StatCard label="Revenue" value={formatINR(d.revenue)} accent="emerald" testid="rep-revenue" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Section icon={Trophy} title="Wins" color="text-emerald-400" testid="report-wins">
              <ul className="space-y-2">{n.wins.map((w, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-emerald-400">▸</span>{w}</li>)}</ul>
            </Section>
            <Section icon={AlertTriangle} title="Concerns" color="text-amber-400" testid="report-concerns">
              <ul className="space-y-2">{n.concerns.map((w, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-amber-400">▸</span>{w}</li>)}</ul>
            </Section>
            <Section icon={Target} title="Next Month Focus" color="text-blue-400" testid="report-focus">
              <ul className="space-y-2">{n.next_month_focus.map((w, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-blue-400">▸</span>{w}</li>)}</ul>
            </Section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Section icon={MessageSquareWarning} title="Objection Analysis" color="text-rose-400" testid="report-objections">
              <p className="text-sm text-slate-300 leading-relaxed mb-4">{n.objection_analysis}</p>
              {d.objections.length === 0 && <div className="text-xs text-slate-500">No objections recorded this month.</div>}
              <div className="space-y-2">
                {d.objections.map((o) => {
                  const max = d.objections[0].count;
                  return (
                    <div key={o.name}>
                      <div className="flex justify-between text-xs text-slate-400 mb-1"><span className="capitalize">{o.name.replace(/_/g, " ")}</span><span>{o.count}</span></div>
                      <div className="h-1.5 rounded-full bg-slate-800"><div className="h-1.5 rounded-full bg-rose-500/70" style={{ width: `${(o.count / max) * 100}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            </Section>
            <Section icon={Trophy} title="Associate Leaderboard" color="text-amber-400" testid="report-leaderboard">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="pb-2">Associate</th><th className="pb-2">Leads</th><th className="pb-2">Meetings</th><th className="pb-2">Won</th><th className="pb-2 text-right">Revenue</th></tr></thead>
                <tbody>
                  {d.leaderboard.map((a, i) => (
                    <tr key={a.name} className="border-t border-slate-800/70">
                      <td className="py-2 text-slate-200">{i === 0 && <span className="text-amber-400 mr-1">★</span>}{a.name}</td>
                      <td className="py-2 text-slate-400">{a.leads}</td><td className="py-2 text-slate-400">{a.meetings}</td>
                      <td className="py-2 text-emerald-400">{a.deals_won}</td><td className="py-2 text-right text-slate-200">{formatINR(a.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex flex-wrap gap-2">
                {d.sources.map((s) => <span key={s.name} className="text-[11px] px-2 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{s.name}: {s.value}</span>)}
              </div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
